import { RangeField } from '@epages/react-components'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import { imageColorTextSettingsDefaults, useColorOptions } from '../../../../../utils/hooks/colorSettings'
import { themeGroupDelimiter } from '../../../../../../utils/themes'
import { useTranslate } from '../../../../../utils/translate'
import CustomSelect from './CustomSelect'
import FormGroup from '../../../../formComponents/FormGroup'
import FormValue from '../../../../formComponents/FormValue'

const colorFields = ['textColor', 'buttonColor', 'backgroundColor']

const colorFieldDependencyMap = {
  textColor: ['text', 'headline'],
  buttonColor: ['buttontext'],
  backgroundColor: ['text', 'headline', 'buttontext'],
}

const isFieldDisabled = (fieldName: string, textInfo: ImmutableMap) => {
  return !colorFieldDependencyMap[fieldName]?.some((propName) => textInfo.get(propName))
}

type ColoredLabelProps = {
  color: ImmutableMap
}

function ColoredLabel({ color }: Readonly<ColoredLabelProps>) {
  return (
    <>
      <div
        className="ep-custom-color-field"
        style={{
          backgroundColor: color.get('value'),
        }}
      />
      <label className="ep-custom-select-label">{color.get('name')}</label>
    </>
  )
}

type RangeSliderWithInputProps = {
  value: number
  setValue: (value: number) => void
  disabled?: boolean
  name: string
  min?: number
  max?: number
  step?: number
  label?: string
}

export function RangeSliderWithInput({
  value,
  name,
  setValue,
  disabled,
  min = 0,
  max = 100,
  step = 1,
  label = '',
}: Readonly<RangeSliderWithInputProps>) {
  const rangeFieldStyle = {
    '--min': min,
    '--max': max,
    '--val': value,
  }

  const labelId = `${name}-input`

  return (
    <div className={cc(['ep-form-row', { disabled }])}>
      {label && (
        <label htmlFor={labelId} className="dali-settingslayer-label">
          {label}
        </label>
      )}
      <div className="dali-range-input-with-numeric">
        <RangeField
          className="dali-range-input"
          {...{ min, name, max, step, disabled, value }}
          style={rangeFieldStyle}
        />
        <input
          id={labelId}
          type="number"
          className="dali-range-output"
          {...{ min, max, step, disabled, value }}
          onChange={(e) => setValue(Number(e.target.value))}
        />
      </div>
    </div>
  )
}

type ImageTextColorSettingsProps = {
  textPersistanceInfo: ImmutableMap
  imageInfo: {
    isSlider: boolean
    src: string
  }
}

export default function ImageTextColorSettings({
  imageInfo,
  textPersistanceInfo,
}: Readonly<ImageTextColorSettingsProps>) {
  const isSlider = Boolean(imageInfo?.isSlider)
  const componentName = isSlider ? 'contentSliderComponent' : 'imageElementComponent'
  const disabled = !imageInfo?.src || !textPersistanceInfo.some((val) => Boolean(val))

  const [themeNameOrThemeGroupName] = useSelector<State, string[]>((state) =>
    state.getIn(['shop', 'theme', 'name']).split(themeGroupDelimiter),
  )

  const t = useTranslate('interface')
  const tScoped = (subKey, ...args) => t(`components.${componentName}.settingsModal.${subKey}`, ...args)
  const tThemeSettings = (subKey, ...args) =>
    t(`themes:${themeNameOrThemeGroupName.replace('.', '_')}.settings.${subKey}`, ...args)

  const colorOptions = useColorOptions(tScoped, tThemeSettings)

  return (
    <>
      {colorFields.map((name) => (
        <FormGroup
          className={cc({ disabled: disabled || isFieldDisabled(name, textPersistanceInfo) })}
          key={name}
          name={`${name}Index`}
          tagName="label"
          label={tScoped(name)}
        >
          <FormValue>
            {(value, setColor) => (
              <CustomSelect
                name={name}
                className="ep-image-text-color-select"
                selectedIndex={value}
                defaultIndex={imageColorTextSettingsDefaults.get(`${name}Index`)}
                options={colorOptions}
                onChange={setColor}
                disabled={disabled || isFieldDisabled(name, textPersistanceInfo)}
                renderOption={(color) => <ColoredLabel color={color} />}
              />
            )}
          </FormValue>
        </FormGroup>
      ))}
      <FormValue name="overlayOpacity">
        {(value, setValue) => (
          <RangeSliderWithInput
            name="overlayOpacity"
            label={tScoped('overlayOpacityInputField.label')}
            {...{ value, setValue, disabled }}
          />
        )}
      </FormValue>
    </>
  )
}
